import {
    Chip,
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Modal,
    ModalContent,
    Button,
    ModalFooter,
    ModalBody,
    ModalHeader,
    Pagination,
    Tabs, Tab, Card, CardBody,
    Input,
    Select,
    SelectItem,
} from "@heroui/react";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Icon } from "@iconify/react";
import { apiClientExporter } from "../../services/apiClient";
import { toast } from "sonner";
import DetalleQuickview from "../../components/claim/DetalleQuickView";
import DetalleMaestraEditar from "./DetalleMaestraEditar";
import DetalleJudicialEditar from "./DetalleJudicialEditar";
import ClaimLetterTab from "./ClaimLetterTab";
import { motion, AnimatePresence } from "framer-motion";
import { generateCopiableName } from "../../utils/general";

function BudgetTable({
    quartersData,
    selectedProjection,
    projectionsList = [],
    profile,
    aCargoFilter = "all",
    aCargoOptions = [],
    myCasesOnly = false,
    filterValue,
    setFilterValue,
    rowsPerPage,
    setRowsPerPage,
    page,
    setPage,
    sortDescriptor,
    setSortDescriptor,
    topContent,
    bottomContent,
    onFilteredDataChange,
    onReloadData,
    onUpdateQuarter
}) {
    //check if user has admin view (profile_id 2, 5 ,6)
    const adminView = (profileid) => {
        if (!profileid) {
            return false;
        } else {
            return profileid === 2 || profileid === 5 || profileid === 6;
        }
    }

    //MANEJO DE ESTADO PARA EDITAR MAESTRA
    const [maestraLoading, setMaestraLoading] = useState(false);
    const [maestraData, setMaestraData] = useState(null);
    const [maestraQvcId, setMaestraQvcId] = useState(null);
    const [isMaestraModalOpen, setIsMaestraModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState("maestra");

    //MANEJO DE ESTADO PARA AGREGAR PROYECCIÓN
    const [isAddProjectionModalOpen, setIsAddProjectionModalOpen] = useState(false);
    const [isCompleteProjectionModalOpen, setIsCompleteProjectionModalOpen] = useState(false);
    const [isCopyPreviousModalOpen, setIsCopyPreviousModalOpen] = useState(false);
    const [projectionType, setProjectionType] = useState(null); // 'quickview_projection' or 'custom_projection'
    const [selectedQuarterForProjection, setSelectedQuarterForProjection] = useState(null);
    const [projectionForm, setProjectionForm] = useState({
        quickview_percent: '',
        fecha_estimada: '',
        quarter: ''
    });
    const [isSavingProjection, setIsSavingProjection] = useState(false); // Estado para bloquear durante el guardado

    //MANEJO DE ESTADO PARA MODAL DE PROYECCIONES (solo filtro "All")
    const [isProjectionsModalOpen, setIsProjectionsModalOpen] = useState(false);
    const [selectedQuarterForProjections, setSelectedQuarterForProjections] = useState(null);

    // Estado de loading local para la tabla (sin afectar toda la página)
    const [isTableReloading, setIsTableReloading] = useState(false);

    // Helper function to check if a projection is complete
    const isProjectionComplete = useCallback((projection) => {
        if (!projection) return false;
        return !!(projection.quickview_percent && projection.fecha_estimada);
    }, []);

    // Helper function to check if previous projections are complete
    const hasCompletePreviousProjections = useCallback((quarter, targetProjectionId) => {
        if (targetProjectionId <= 1) return true; // First projection always allowed

        // Check quickview_projection (equivalent to id=1)
        const hasCompleteQuickview =
            (quarter.quickview_projection && isProjectionComplete(quarter.quickview_projection)) ||
            (quarter.custom_projections?.some(proj => proj.projection_id === 1 && isProjectionComplete(proj)));

        if (!hasCompleteQuickview) return false;

        // Check all projections between 1 and targetProjectionId
        for (let id = 2; id < targetProjectionId; id++) {
            const projection = quarter.custom_projections?.find(proj => proj.projection_id === id);
            if (!projection || !isProjectionComplete(projection)) {
                return false;
            }
        }

        return true;
    }, [isProjectionComplete]);

    // Generar opciones de quarters (años y trimestres)
    const quarterOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const quarters = [];

        // Generar quarters para los próximos 5 años
        for (let year = currentYear; year <= currentYear + 5; year++) {
            for (let q = 1; q <= 4; q++) {
                quarters.push({
                    key: `${year}-Q${q}`,
                    label: `${year} Q${q}`
                });
            }
        }

        return quarters;
    }, []);

    // Define table columns for quarters
    const columns = useMemo(() => {
        const baseColumns = [
            { name: "RSK Ref.", uid: "rsk_ref", sortable: true },
            { name: "Exporter Name", uid: "exporter_name", sortable: true },
            { name: "A Cargo", uid: "a_cargo", sortable: true },
            { name: "Vessel", uid: "vessel", sortable: true },
            { name: "Container", uid: "container_number", sortable: true },
            { name: "Carrier", uid: "carrier_name", sortable: true },
            { name: "Claim Amount", uid: "claimamount", sortable: true },
            { name: "Status", uid: "status", sortable: true },
            { name: "Current Projection", uid: "current_projection", sortable: true },
            { name: "Current Recovery", uid: "current_recovery", sortable: true },
            { name: "Current Est. Date", uid: "current_estimated_date", sortable: true },
            { name: "Sel. Recovery", uid: "recovery_percent", sortable: true },
            { name: "Sel. Est. Date", uid: "estimated_date", sortable: true },
            { name: "Actions", uid: "actions", sortable: false },
        ];
        return baseColumns;
    }, []);

    // Process quarters data to include projection data and apply filtering
    // quartersData ahora es el array normalizado de quarters (ya no es un objeto)
    const processedData = useMemo(() => {
        if (!quartersData || !Array.isArray(quartersData) || quartersData.length === 0) return [];

        console.log("quartersDataXX", quartersData);

        const projectionId = parseInt(selectedProjection);

        let processed = quartersData.map(quarter => {
            let matchingData = null;
            let projectionState = null; // 'incomplete', 'missing', 'complete', 'not_applicable'
            let shouldInclude = true; // Por defecto incluir todos

            // SIEMPRE mostrar todos los casos - Buscar la proyección seleccionada para mostrar en columnas
            // FILTRO "All" (id=0): No hay proyección seleccionada específica
            if (projectionId === 0) {
                // No hay proyección seleccionada, no mostrar datos en columnas seleccionadas
                matchingData = null;
                projectionState = 'not_applicable';
                shouldInclude = true; // Siempre incluir cuando es "All"
            }
            // Buscar QuickView (id=1)
            else if (projectionId === 1) {
                const quickviewProj = quarter.quickview_projection;
                const customQuickview = quarter.custom_projections?.find(proj => proj.projection_id === 1);

                // Verificar si QuickView está completo (en cualquiera de sus formas)
                const hasQuickviewProjComplete = quickviewProj && isProjectionComplete(quickviewProj);
                const hasCustomQuickviewComplete = customQuickview && isProjectionComplete(customQuickview);

                // Verificar si tiene proyecciones de nivel superior (2, 3, etc.)
                // Si tiene proyecciones superiores, significa que QuickView ya fue completado
                const hasHigherProjections = quarter.custom_projections?.some(proj =>
                    proj.projection_id && proj.projection_id > 1
                );

                // EXCLUIR si tiene QuickView completo (en cualquiera de sus formas)
                // O si tiene proyecciones de nivel superior (indica que QuickView ya fue completado)
                if (hasQuickviewProjComplete || hasCustomQuickviewComplete || hasHigherProjections) {
                    shouldInclude = false;
                } else {
                    // Priorizar quickview_projection si existe
                    if (quickviewProj) {
                        matchingData = quickviewProj;
                        projectionState = isProjectionComplete(quickviewProj) ? 'complete' : 'incomplete';
                    }
                    // Si no, buscar custom_projections con projection_id=1
                    else if (customQuickview) {
                        matchingData = customQuickview;
                        projectionState = isProjectionComplete(customQuickview) ? 'complete' : 'incomplete';
                    }
                    // Si no tiene ninguna, estado missing
                    else {
                        matchingData = null;
                        projectionState = 'missing';
                    }
                }
            }
            // Buscar proyecciones tipo 2 y siguientes
            else {
                // Buscar la proyección con el projection_id seleccionado
                const selectedProjection = quarter.custom_projections?.find(proj => proj.projection_id === projectionId);

                if (selectedProjection) {
                    // EXCLUIR si la proyección seleccionada está completa
                    if (isProjectionComplete(selectedProjection)) {
                        shouldInclude = false;
                    } else {
                        matchingData = selectedProjection;
                        projectionState = 'incomplete';
                    }
                } else {
                    // No tiene la proyección seleccionada
                    matchingData = null;
                    projectionState = 'missing';
                    shouldInclude = true; // Incluir para poder agregarla
                }
            }

            // Calcular la proyección más alta (por projection_id)
            let highestProjectionId = null;
            let highestProjectionName = 'N/A';
            let highestProjectionData = null; // Datos completos de la proyección más alta

            // Si tiene quickview_projection, considerar projection_id = 1
            if (quarter.quickview_projection) {
                highestProjectionId = 1;
                highestProjectionData = quarter.quickview_projection;
            }

            // Revisar custom_projections para encontrar el projection_id más alto
            if (quarter.custom_projections && quarter.custom_projections.length > 0) {
                const customProjectionIds = quarter.custom_projections
                    .map(proj => proj.projection_id)
                    .filter(id => id !== null && id !== undefined);

                if (customProjectionIds.length > 0) {
                    const maxCustomId = Math.max(...customProjectionIds);
                    if (!highestProjectionId || maxCustomId > highestProjectionId) {
                        highestProjectionId = maxCustomId;
                        // Encontrar la proyección con el ID más alto
                        highestProjectionData = quarter.custom_projections.find(proj => proj.projection_id === maxCustomId);
                    }
                }
            }

            // Buscar el nombre de la proyección en projectionsList
            if (highestProjectionId !== null && projectionsList.length > 0) {
                const projectionName = projectionsList.find(p => p.id === highestProjectionId);
                highestProjectionName = projectionName?.title || `Projection ${highestProjectionId}`;
            }

            // Calcular recovery y fecha estimada de la proyección más alta
            let currentRecovery = 'N/A';
            let currentEstimatedDate = 'N/A';

            if (highestProjectionData) {
                // Calcular recovery percentage
                if (highestProjectionData.quickview_percent !== null && highestProjectionData.quickview_percent !== undefined) {
                    // quickview_percent viene como decimal (0.82) o puede venir ya como porcentaje
                    const percentValue = parseFloat(highestProjectionData.quickview_percent);
                    // Si es menor que 1, multiplicar por 100, si no, usar directamente
                    currentRecovery = (percentValue < 1 ? percentValue * 100 : percentValue).toFixed(2) + '%';
                }

                // Obtener fecha estimada
                if (highestProjectionData.fecha_estimada) {
                    currentEstimatedDate = highestProjectionData.fecha_estimada;
                }
            }

            // Calcular recovery y fecha estimada de la proyección seleccionada (matchingData)
            let selectedRecovery = 'N/A';
            let selectedEstimatedDate = 'N/A';

            if (matchingData && projectionId !== 0) {
                // Calcular recovery percentage de la proyección seleccionada
                if (matchingData.quickview_percent !== null && matchingData.quickview_percent !== undefined) {
                    // quickview_percent viene como decimal (0.82) o puede venir ya como porcentaje
                    const percentValue = parseFloat(matchingData.quickview_percent);
                    // Si es menor que 1, multiplicar por 100, si no, usar directamente
                    selectedRecovery = (percentValue < 1 ? percentValue * 100 : percentValue).toFixed(2) + '%';
                }

                // Obtener fecha estimada de la proyección seleccionada
                if (matchingData.fecha_estimada) {
                    selectedEstimatedDate = matchingData.fecha_estimada;
                }
            }

            return {
                ...quarter,
                recovery_percent: selectedRecovery, // Recovery de la proyección seleccionada
                estimated_date: selectedEstimatedDate, // Fecha estimada de la proyección seleccionada
                hasProjection: !!matchingData && projectionId !== 0, // Tiene la proyección seleccionada
                projectionId: matchingData?.id,
                projectionState, // Estado de la proyección seleccionada
                matchingProjection: matchingData, // Datos de la proyección seleccionada
                current_projection: highestProjectionName, // Nombre de la proyección más alta
                current_projection_id: highestProjectionId, // ID de la proyección más alta (para ordenamiento)
                current_recovery: currentRecovery, // Recovery percentage de la proyección más alta
                current_estimated_date: currentEstimatedDate, // Fecha estimada de la proyección más alta
                selectedProjectionId: projectionId, // ID de la proyección seleccionada (para botones de acción)
                shouldInclude // Incluir o no según si la proyección seleccionada está completa
            };
        });

        // Filtrar casos que tienen la proyección seleccionada completa (excepto cuando es "All")
        processed = processed.filter(quarter => quarter.shouldInclude);

        // Filtro "My Cases" (igual que ClaimsTable: iniciales vs a_cargo, admins ven todo)
        if (myCasesOnly) {
            processed = processed.filter((row) => {
                if (!row) return false;
                if (adminView(profile?.profile_id)) return true;
                if (profile?.first_name && profile?.last_name && row.a_cargo) {
                    const userInitials = `${profile.first_name.charAt(0).toUpperCase()}${profile.last_name.charAt(0).toUpperCase()}`;
                    return row.a_cargo === userInitials;
                }
                return false;
            });
        }

        // Filtro por A Cargo (selector)
        if (aCargoFilter && aCargoFilter !== "all" && aCargoFilter !== null) {
            processed = processed.filter((row) => {
                if (!row) return false;
                return row.a_cargo === aCargoFilter;
            });
        }

        // Apply search filter
        if (filterValue) {
            const lowerCaseFilter = filterValue.toLowerCase();
            const normalizedFilter = lowerCaseFilter.replace(/-/g, "/");
            processed = processed.filter((quarter) => {
                // Coincidencia especial para rsk_ref permitiendo '-' como '/'
                const rsk = (quarter.rsk_ref || "").toString().toLowerCase();
                const rskNormalized = rsk.replace(/-/g, "/");
                const matchesRsk = rsk.includes(normalizedFilter) || rskNormalized.includes(normalizedFilter);
                if (matchesRsk) return true;

                return [
                    quarter.exporter_name,
                    quarter.a_cargo,
                    quarter.vessel,
                    quarter.container_number,
                    quarter.carrier_name,
                    quarter.claimamount?.toString(),
                    quarter.status,
                    quarter.current_projection,
                    quarter.current_recovery,
                    quarter.current_estimated_date,
                    quarter.recovery_percent,
                    quarter.estimated_date
                ].some((value) => value?.toString().toLowerCase().includes(lowerCaseFilter));
            });
        }

        return processed;
    }, [quartersData, selectedProjection, filterValue, isProjectionComplete, hasCompletePreviousProjections, projectionsList, myCasesOnly, profile, aCargoFilter]);

    // Sort processed data based on sortDescriptor
    const sortedData = useMemo(() => {
        if (!sortDescriptor.column) return processedData;

        return [...processedData].sort((a, b) => {
            const first = a[sortDescriptor.column];
            const second = b[sortDescriptor.column];

            // Handle special cases for recovery_percent and estimated_date
            if (sortDescriptor.column === "recovery_percent") {
                const numA = parseFloat(a.recovery_percent.replace('%', '')) || 0;
                const numB = parseFloat(b.recovery_percent.replace('%', '')) || 0;
                const cmp = numA - numB;
                return sortDescriptor.direction === "descending" ? -cmp : cmp;
            }

            if (sortDescriptor.column === "estimated_date") {
                // Sort by date string (YYYY-Q1 format)
                const cmp = first < second ? -1 : first > second ? 1 : 0;
                return sortDescriptor.direction === "descending" ? -cmp : cmp;
            }

            if (sortDescriptor.column === "current_projection") {
                // Sort by projection ID (numeric) for proper ordering
                const idA = a.current_projection_id ?? 0;
                const idB = b.current_projection_id ?? 0;
                const cmp = idA - idB;
                return sortDescriptor.direction === "descending" ? -cmp : cmp;
            }

            if (sortDescriptor.column === "current_recovery") {
                // Sort by recovery percentage (numeric)
                const numA = parseFloat(a.current_recovery.replace('%', '')) || 0;
                const numB = parseFloat(b.current_recovery.replace('%', '')) || 0;
                const cmp = numA - numB;
                return sortDescriptor.direction === "descending" ? -cmp : cmp;
            }

            if (sortDescriptor.column === "current_estimated_date") {
                // Sort by date string (YYYY-Q1 format)
                const cmp = a.current_estimated_date < b.current_estimated_date ? -1 : a.current_estimated_date > b.current_estimated_date ? 1 : 0;
                return sortDescriptor.direction === "descending" ? -cmp : cmp;
            }

            // Default sorting
            const cmp = first < second ? -1 : first > second ? 1 : 0;
            return sortDescriptor.direction === "descending" ? -cmp : cmp;
        });
    }, [processedData, sortDescriptor]);

    // Reset page to 1 when projection changes
    useEffect(() => {
        setPage(1);
    }, [selectedProjection]);

    // Notify parent component of filtered data length
    useEffect(() => {
        if (onFilteredDataChange) {
            onFilteredDataChange(sortedData.length);
            const projectionTitle = projectionsList.find(p => p.id === parseInt(selectedProjection))?.title || selectedProjection;
            console.log(`Updated filtered quarters count: ${sortedData.length} for projection id: ${selectedProjection} (${projectionTitle})`);
        }
    }, [sortedData.length, onFilteredDataChange, selectedProjection, projectionsList]);

    // Calculate pagination
    const pages = Math.ceil(sortedData.length / rowsPerPage) || 1;

    const items = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        const end = start + rowsPerPage;
        return sortedData.slice(start, end);
    }, [page, sortedData, rowsPerPage]);

    const closeMaestraModal = () => {
        setIsMaestraModalOpen(false);
        setMaestraData(null);
        setMaestraQvcId(null);
        setSelectedTab("maestra");
    }

    const handleEditMaster = async (id, qvcId) => {
        if (!id || maestraLoading) {
            alert('operacion no valida!')
            return
        }
        setMaestraLoading(true)

        try {
            const response = await apiClientExporter.get(`/exporter/maestra?id=${id}`);
            console.log("response", response);
            const data = response.data;
            setMaestraData(data)
            setMaestraQvcId(qvcId)
            console.log("datamodal", data);
            console.log("qvcId del row:", qvcId);
            setIsMaestraModalOpen(true);
        } catch (err) {
            console.error(err.message);
        } finally {
            console.log("Finalizando editar Maestra");
            setMaestraLoading(false)
        }
    };

    // Helper function to find previous projection
    const findPreviousProjection = useCallback((quarter, currentProjectionId) => {
        if (currentProjectionId <= 1) return null; // No hay proyección anterior a QuickView

        // Buscar la proyección anterior (projection_id - 1)
        const previousProjectionId = currentProjectionId - 1;

        // Si es projection_id = 2, buscar QuickView (id=1)
        if (previousProjectionId === 1) {
            if (quarter.quickview_projection) {
                return quarter.quickview_projection;
            }
            // Buscar custom_projection con projection_id = 1
            return quarter.custom_projections?.find(proj => proj.projection_id === 1) || null;
        }

        // Buscar en custom_projections
        return quarter.custom_projections?.find(proj => proj.projection_id === previousProjectionId) || null;
    }, []);

    const handleAddProjection = (quarter) => {
        setSelectedQuarterForProjection(quarter);
        setProjectionForm({
            quickview_percent: '',
            fecha_estimada: '',
            quarter: ''
        });
        setIsAddProjectionModalOpen(true);
    };

    const handleCopyPreviousProjection = (quarter) => {
        const projectionId = parseInt(selectedProjection);
        const previousProjection = findPreviousProjection(quarter, projectionId);

        if (!previousProjection) {
            toast.error('No previous projection found');
            return;
        }

        setSelectedQuarterForProjection(quarter);
        setIsCopyPreviousModalOpen(true);
    };

    const handleConfirmCopyPrevious = async () => {
        if (!selectedQuarterForProjection) return;

        const projectionId = parseInt(selectedProjection);
        const previousProjection = findPreviousProjection(selectedQuarterForProjection, projectionId);

        if (!previousProjection) {
            toast.error('No previous projection found');
            setIsCopyPreviousModalOpen(false);
            return;
        }

        // Copiar los datos de la proyección anterior
        const previousPercent = previousProjection.quickview_percent
            ? (parseFloat(previousProjection.quickview_percent) * 100).toString()
            : '';
        const previousDate = previousProjection.fecha_estimada || '';

        // Llenar el formulario con los datos anteriores y abrir el modal de agregar
        setProjectionForm({
            quickview_percent: previousPercent,
            fecha_estimada: previousDate,
            quarter: previousDate
        });

        setIsCopyPreviousModalOpen(false);
        setIsAddProjectionModalOpen(true);
    };

    const handleSaveProjection = async () => {
        if (!selectedQuarterForProjection || !projectionForm.quickview_percent || !projectionForm.quarter) {
            toast.error('Please complete all fields');
            return;
        }

        // Get projection title from id
        const projectionId = parseInt(selectedProjection);
        const selectedProj = projectionsList.find(p => p.id === projectionId);
        const projectionTitle = selectedProj?.title || 'Unknown';

        // Guardar el estado anterior para poder revertir si hay error
        const quarterId = selectedQuarterForProjection.id;
        const previousQuarterState = quartersData.find(q => q.id === quarterId);

        setIsSavingProjection(true);

        try {
            const projectionData = {
                maestra_id: selectedQuarterForProjection.id,
                title: projectionTitle,
                quickview_percent: parseFloat(projectionForm.quickview_percent) / 100, // Convertir a fracción
                fecha_estimada: projectionForm.quarter,
                author: `${profile?.first_name} ${profile?.last_name}`,
                user_email: profile?.email,
                exporter_id: profile?.id,
                projection_id: projectionId // Incluir projection_id para relacionar con la tabla projections
            };

            const response = await apiClientExporter.post('/exporter/projection', projectionData);
            const newProjection = response.data?.projection;

            // Actualizar solo el quarter específico en el estado local (actualización optimista)
            if (onUpdateQuarter && newProjection) {
                onUpdateQuarter(quarterId, (quarter) => ({
                    ...quarter,
                    custom_projections: [
                        ...(quarter.custom_projections || []),
                        {
                            ...newProjection,
                            projection_id: projectionId,
                            title: projectionTitle
                        }
                    ]
                }));
            } else if (onReloadData) {
                // Fallback: recarga completa si no hay función de actualización específica
                setIsTableReloading(true);
                try {
                    await onReloadData();
                } finally {
                    setIsTableReloading(false);
                }
            }

            // Cerrar modal y limpiar formulario después de éxito
            setIsAddProjectionModalOpen(false);
            setSelectedQuarterForProjection(null);
            setProjectionForm({ quickview_percent: '', fecha_estimada: '', quarter: '' });

            toast.success('Projection added successfully');

        } catch (error) {
            console.error('Error al guardar proyección:', error);

            // Revertir al estado anterior si hay error (aunque en este caso no hubo cambio previo)
            // No es necesario revertir porque no se agregó nada aún, pero lo dejamos por consistencia
            if (onUpdateQuarter && previousQuarterState) {
                onUpdateQuarter(quarterId, () => previousQuarterState);
            }

            toast.error('Error saving projection. Changes have been reverted.');
        } finally {
            setIsSavingProjection(false);
        }
    };

    const closeAddProjectionModal = () => {
        if (isSavingProjection) return; // No permitir cerrar mientras se guarda
        setIsAddProjectionModalOpen(false);
        setSelectedQuarterForProjection(null);
        setProjectionForm({ quickview_percent: '', fecha_estimada: '', quarter: '' });
    };

    // Handler para ver todas las proyecciones (filtro "All")
    const handleViewProjections = (quarter) => {
        setSelectedQuarterForProjections(quarter);
        setIsProjectionsModalOpen(true);
    };

    const closeProjectionsModal = () => {
        setIsProjectionsModalOpen(false);
        setSelectedQuarterForProjections(null);
    };

    // Handler para completar proyección
    const handleCompleteProjection = (quarter) => {
        const projectionId = parseInt(selectedProjection);

        if (projectionId === 1) {
            // QuickView: verificar si es quickview_projection o custom
            if (quarter.quickview_projection && !isProjectionComplete(quarter.quickview_projection)) {
                // Es quickview_projection, solo fecha_estimada editable
                setProjectionType('quickview_projection');
                setProjectionForm({
                    quickview_percent: '', // No editable
                    fecha_estimada: quarter.quickview_projection.fecha_estimada || '',
                    quarter: quarter.quickview_projection.fecha_estimada || ''
                });
            } else {
                // Es custom_projections con projection_id = 1
                const customProj = quarter.custom_projections?.find(proj => proj.projection_id === 1);
                setProjectionType('custom_projection');
                setProjectionForm({
                    quickview_percent: customProj?.quickview_percent ? (parseFloat(customProj.quickview_percent) * 100).toString() : '',
                    fecha_estimada: customProj?.fecha_estimada || '',
                    quarter: customProj?.fecha_estimada || ''
                });
            }
        } else {
            // Es custom_projection para otro tipo
            const customProj = quarter.custom_projections?.find(proj => proj.projection_id === projectionId);
            setProjectionType('custom_projection');
            setProjectionForm({
                quickview_percent: customProj?.quickview_percent ? (parseFloat(customProj.quickview_percent) * 100).toString() : '',
                fecha_estimada: customProj?.fecha_estimada || '',
                quarter: customProj?.fecha_estimada || ''
            });
        }

        setSelectedQuarterForProjection(quarter);
        setIsCompleteProjectionModalOpen(true);
    };

    // Handler para guardar proyección completada
    const handleSaveCompleteProjection = async () => {
        if (!selectedQuarterForProjection) {
            toast.error('No quarter selected');
            return;
        }

        if (projectionType === 'quickview_projection') {
            // Solo actualizar fecha_estimada via PUT /exporter/maestra/recuperos
            if (!projectionForm.quarter) {
                toast.error('Please select estimated date');
                return;
            }

            // Guardar el estado anterior para poder revertir si hay error
            const quarterId = selectedQuarterForProjection.id;
            const previousQuarterState = quartersData.find(q => q.id === quarterId);
            const previousFechaEstimada = previousQuarterState?.quickview_projection?.fecha_estimada;

            setIsSavingProjection(true);

            try {
                const maestraData = await apiClientExporter.get(`/exporter/maestra?id=${selectedQuarterForProjection.id}`);
                const currentData = maestraData.data;

                await apiClientExporter.put('/exporter/maestra/recuperos', {
                    ...currentData,
                    fecha_estimada: projectionForm.quarter
                });

                // Actualizar solo el quarter específico en el estado local (actualización optimista)
                if (onUpdateQuarter) {
                    onUpdateQuarter(quarterId, (quarter) => ({
                        ...quarter,
                        quickview_projection: quarter.quickview_projection ? {
                            ...quarter.quickview_projection,
                            fecha_estimada: projectionForm.quarter
                        } : null
                    }));
                } else if (onReloadData) {
                    // Fallback: recarga completa si no hay función de actualización específica
                    setIsTableReloading(true);
                    try {
                        await onReloadData();
                    } finally {
                        setIsTableReloading(false);
                    }
                }

                // Cerrar modal y limpiar formulario después de éxito
                setIsCompleteProjectionModalOpen(false);
                setSelectedQuarterForProjection(null);
                setProjectionForm({ quickview_percent: '', fecha_estimada: '', quarter: '' });

                toast.success('QuickView projection updated successfully');

            } catch (error) {
                console.error('Error updating quickview_projection:', error);

                // Revertir al estado anterior si hay error
                if (onUpdateQuarter && previousQuarterState) {
                    onUpdateQuarter(quarterId, () => previousQuarterState);
                }

                toast.error('Error updating projection. Changes have been reverted.');
            } finally {
                setIsSavingProjection(false);
            }
        } else {
            // custom_projection: actualizar via PUT /exporter/projections/:projectionId
            if (!projectionForm.quickview_percent || !projectionForm.quarter) {
                toast.error('Please complete all fields');
                return;
            }

            // Si viene del modal de Proyecciones, usar el ID guardado
            const projectionIdToEdit = selectedQuarterForProjection._editingProjectionId;

            if (projectionIdToEdit) {
                // Editar proyección existente desde el modal de Proyecciones
                // Guardar el estado anterior para poder revertir si hay error
                const quarterId = selectedQuarterForProjection.id;
                const previousQuarterState = quartersData.find(q => q.id === quarterId);

                setIsSavingProjection(true);

                try {
                    await apiClientExporter.put(`/exporter/projections/${projectionIdToEdit}`, {
                        quickview_percent: parseFloat(projectionForm.quickview_percent) / 100,
                        fecha_estimada: projectionForm.quarter
                    });

                    // Actualizar solo el quarter específico en el estado local (actualización optimista)
                    if (onUpdateQuarter) {
                        onUpdateQuarter(quarterId, (quarter) => ({
                            ...quarter,
                            custom_projections: quarter.custom_projections?.map(proj =>
                                proj.id === projectionIdToEdit ? {
                                    ...proj,
                                    quickview_percent: (parseFloat(projectionForm.quickview_percent) / 100).toString(),
                                    fecha_estimada: projectionForm.quarter,
                                    proyeccion_usd: quarter.claimamount ?
                                        (quarter.claimamount * parseFloat(projectionForm.quickview_percent) / 100).toFixed(2) :
                                        proj.proyeccion_usd
                                } : proj
                            ) || []
                        }));
                    } else if (onReloadData) {
                        // Fallback: recarga completa si no hay función de actualización específica
                        setIsTableReloading(true);
                        try {
                            await onReloadData();
                        } finally {
                            setIsTableReloading(false);
                        }
                    }

                    // Cerrar modal y limpiar formulario después de éxito
                    setIsCompleteProjectionModalOpen(false);
                    const projectionIdType = selectedQuarterForProjection._editingProjectionIdType;
                    setSelectedQuarterForProjection(null);
                    setProjectionForm({ quickview_percent: '', fecha_estimada: '', quarter: '' });

                    toast.success('Projection updated successfully');

                } catch (error) {
                    console.error('Error updating custom_projection:', error);

                    // Revertir al estado anterior si hay error
                    if (onUpdateQuarter && previousQuarterState) {
                        onUpdateQuarter(quarterId, () => previousQuarterState);
                    }

                    toast.error('Error updating projection. Changes have been reverted.');
                } finally {
                    setIsSavingProjection(false);
                }
            } else {
                // Buscar por projection_id (desde botón "Complete Projection")
                const projectionId = parseInt(selectedProjection);
                const customProj = selectedQuarterForProjection.custom_projections?.find(proj => proj.projection_id === projectionId);

                if (!customProj || !customProj.id) {
                    // No existe, crear nueva
                    await handleSaveProjection();
                    // handleSaveProjection ya maneja el cierre del modal y recarga de datos
                    setIsCompleteProjectionModalOpen(false);
                    return;
                }

                // Guardar el estado anterior para poder revertir si hay error
                const quarterId = selectedQuarterForProjection.id;
                const previousQuarterState = quartersData.find(q => q.id === quarterId);

                setIsSavingProjection(true);

                try {
                    await apiClientExporter.put(`/exporter/projections/${customProj.id}`, {
                        quickview_percent: parseFloat(projectionForm.quickview_percent) / 100,
                        fecha_estimada: projectionForm.quarter
                    });

                    // Actualizar solo el quarter específico en el estado local (actualización optimista)
                    if (onUpdateQuarter) {
                        onUpdateQuarter(quarterId, (quarter) => ({
                            ...quarter,
                            custom_projections: quarter.custom_projections?.map(proj =>
                                proj.id === customProj.id ? {
                                    ...proj,
                                    quickview_percent: (parseFloat(projectionForm.quickview_percent) / 100).toString(),
                                    fecha_estimada: projectionForm.quarter,
                                    proyeccion_usd: quarter.claimamount ?
                                        (quarter.claimamount * parseFloat(projectionForm.quickview_percent) / 100).toFixed(2) :
                                        proj.proyeccion_usd
                                } : proj
                            ) || []
                        }));
                    } else if (onReloadData) {
                        // Fallback: recarga completa si no hay función de actualización específica
                        setIsTableReloading(true);
                        try {
                            await onReloadData();
                        } finally {
                            setIsTableReloading(false);
                        }
                    }

                    // Cerrar modal y limpiar formulario después de éxito
                    setIsCompleteProjectionModalOpen(false);
                    setSelectedQuarterForProjection(null);
                    setProjectionForm({ quickview_percent: '', fecha_estimada: '', quarter: '' });

                    toast.success('Projection updated successfully');

                } catch (error) {
                    console.error('Error updating custom_projection:', error);

                    // Revertir al estado anterior si hay error
                    if (onUpdateQuarter && previousQuarterState) {
                        onUpdateQuarter(quarterId, () => previousQuarterState);
                    }

                    toast.error('Error updating projection. Changes have been reverted.');
                } finally {
                    setIsSavingProjection(false);
                }
            }
        }
    };

    const closeCompleteProjectionModal = () => {
        if (isSavingProjection) return; // No permitir cerrar mientras se guarda
        setIsCompleteProjectionModalOpen(false);
        setSelectedQuarterForProjection(null);
        setProjectionType(null);
        setProjectionForm({ quickview_percent: '', fecha_estimada: '', quarter: '' });
    };

    // Handlers para editar desde el modal de Proyecciones (filtro "All")
    const handleEditQuickviewProjection = (quarter) => {
        closeProjectionsModal();
        setProjectionType('quickview_projection');
        setProjectionForm({
            quickview_percent: '', // No editable
            fecha_estimada: quarter.quickview_projection?.fecha_estimada || '',
            quarter: quarter.quickview_projection?.fecha_estimada || ''
        });
        setSelectedQuarterForProjection(quarter);
        setIsCompleteProjectionModalOpen(true);
    };

    const handleEditCustomProjection = (quarter, projection) => {
        closeProjectionsModal();
        setProjectionType('custom_projection');
        setProjectionForm({
            quickview_percent: projection?.quickview_percent ? (parseFloat(projection.quickview_percent) * 100).toString() : '',
            fecha_estimada: projection?.fecha_estimada || '',
            quarter: projection?.fecha_estimada || ''
        });
        // Necesitamos guardar el projection_id para que handleSaveCompleteProjection pueda encontrarla
        const quarterWithSelectedProj = {
            ...quarter,
            _editingProjectionId: projection.id,
            _editingProjectionIdType: projection.projection_id
        };
        setSelectedQuarterForProjection(quarterWithSelectedProj);
        setIsCompleteProjectionModalOpen(true);
    };

    return (
        <>
            <style>{`
                .sticky-header thead {
                    position: sticky;
                    top: 0;
                    z-index: 10;
                    background-color: white;
                }
                .dark .sticky-header thead {
                    background-color: rgb(17, 24, 39);
                }
            `}</style>
            <div className="relative">
                {isTableReloading && (
                    <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 z-50 flex items-center justify-center rounded-lg">
                        <div className="flex flex-col items-center gap-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Updating table...</p>
                        </div>
                    </div>
                )}
                <Table
                    aria-label="Quarters details table"
                    isStriped
                    isVirtualized
                    bottomContent={bottomContent}
                    bottomContentPlacement="outside"
                    topContent={topContent}
                    topContentPlacement="outside"
                    sortDescriptor={sortDescriptor}
                    onSortChange={setSortDescriptor}
                    classNames={{
                        wrapper: "h-[calc(100vh-226px)] sticky-header",
                    }}
                >
                <TableHeader columns={columns}>
                    {(column) => (
                        <TableColumn
                            key={column.uid}
                            allowsSorting={column.sortable}
                        >
                            {column.name}
                        </TableColumn>
                    )}
                </TableHeader>
                <TableBody
                    emptyContent="No quarters data available"
                    items={items || []}
                >
                    {(row) => (
                        <TableRow
                            key={row?.id || Math.random()}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {columns.map((column) => (
                                <TableCell key={column.uid}>
                                    {column.uid === "claimamount" ? (
                                        `$${new Intl.NumberFormat("es-CL", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(row?.claimamount || 0)}`
                                    ) : column.uid === "actions" ? (
                                        <div className="flex gap-2">
                                            <Chip
                                                color="primary"
                                                size="sm"
                                                isDisabled={maestraLoading}
                                                onClick={() => handleEditMaster(row.id, row.qvc_id)}
                                                role="button"
                                                className="cursor-pointer hover:bg-opacity-80 active:scale-95 transition-all"
                                                aria-label={`View details for ${row?.rsk_ref || "unknown"}`}
                                            >
                                                Open
                                            </Chip>
                                            {/* Botón "Projections" solo para filtro "All" (id=0) */}
                                            {parseInt(selectedProjection) === 0 && (
                                                <Chip
                                                    color="warning"
                                                    size="sm"
                                                    onClick={() => handleViewProjections(row)}
                                                    role="button"
                                                    className="cursor-pointer hover:bg-opacity-80 active:scale-95 transition-all"
                                                    aria-label={`View projections for ${row?.rsk_ref || "unknown"}`}
                                                >
                                                    Projections
                                                </Chip>
                                            )}
                                            {/* Botones contextuales según estado de proyección seleccionada (no en filtro "All") */}
                                            {parseInt(selectedProjection) !== 0 && (
                                                <>
                                                    {row.projectionState === 'incomplete' && (
                                                        <Chip
                                                            color="warning"
                                                            size="sm"
                                                            onClick={() => handleCompleteProjection(row)}
                                                            role="button"
                                                            className="cursor-pointer hover:bg-opacity-80 active:scale-95 transition-all"
                                                            aria-label={`Complete selected projection for ${row?.rsk_ref || "unknown"}`}
                                                        >
                                                            Complete Projection
                                                        </Chip>
                                                    )}
                                                    {row.projectionState === 'missing' && (
                                                        <>
                                                            <Chip
                                                                color="success"
                                                                size="sm"
                                                                onClick={() => handleAddProjection(row)}
                                                                role="button"
                                                                className="cursor-pointer hover:bg-opacity-80 active:scale-95 transition-all"
                                                                aria-label={`Add selected projection for ${row?.rsk_ref || "unknown"}`}
                                                            >
                                                                Add Projection
                                                            </Chip>
                                                            {findPreviousProjection(row, parseInt(selectedProjection)) && (
                                                                <Chip
                                                                    color="secondary"
                                                                    size="sm"
                                                                    onClick={() => handleCopyPreviousProjection(row)}
                                                                    role="button"
                                                                    className="cursor-pointer hover:bg-opacity-80 active:scale-95 transition-all"
                                                                    aria-label={`Copy previous projection for ${row?.rsk_ref || "unknown"}`}
                                                                >
                                                                    Copy Previous
                                                                </Chip>
                                                            )}
                                                        </>
                                                    )}
                                                    {/* No mostrar botones si la proyección está completa o no aplica */}
                                                </>
                                            )}
                                        </div>
                                    ) : column.uid === "current_projection" ? (
                                        <span className={row.current_projection_id ? "text-purple-600 font-semibold" : "text-gray-500"}>
                                            {row.current_projection || "N/A"}
                                        </span>
                                    ) : column.uid === "current_recovery" ? (
                                        <span className={row.current_projection_id ? "text-green-600 font-semibold" : "text-gray-500"}>
                                            {row.current_recovery || "N/A"}
                                        </span>
                                    ) : column.uid === "current_estimated_date" ? (
                                        <span className={row.current_projection_id ? "text-blue-600 font-semibold" : "text-gray-500"}>
                                            {row.current_estimated_date || "N/A"}
                                        </span>
                                    ) : column.uid === "recovery_percent" ? (
                                        <span className={row.hasProjection && row.selectedProjectionId !== 0 ? "text-green-600 font-semibold" : "text-gray-500"}>
                                            {row.recovery_percent}
                                        </span>
                                    ) : column.uid === "estimated_date" ? (
                                        <span className={row.hasProjection && row.selectedProjectionId !== 0 ? "text-blue-600 font-semibold" : "text-gray-500"}>
                                            {row.estimated_date}
                                        </span>
                                    ) : (
                                        row?.[column.uid] || "N/A"
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            </div>

            {/* Maestra Edit Modal */}
            <Modal
                isOpen={isMaestraModalOpen}
                onClose={closeMaestraModal}
                size="full"
                className="max-h-screen overflow-y-auto"
                scrollBehavior="inside"
                key={isMaestraModalOpen}
            >
                <ModalContent>
                    <>
                        <ModalHeader>
                            <div className="flex flex-col items-center w-full gap-0">
                                <div className="flex justify-center">
                                    <Tabs
                                        aria-label="Opciones de Planilla"
                                        selectedKey={selectedTab}
                                        onSelectionChange={setSelectedTab}
                                    >
                                        <Tab key="maestra" title="Extra Judicial" />
                                        <Tab key="judicial" title="Judicial" />
                                        <Tab
                                            key="quickview"
                                            title="QuickView"
                                            isDisabled={!maestraQvcId}
                                            className={!maestraQvcId ? "opacity-50 text-gray-400" : ""}
                                        />
                                        <Tab
                                            key="claimletter"
                                            title="Claim Letter"
                                            isDisabled={!maestraQvcId}
                                            className={!maestraQvcId ? "opacity-50 text-gray-400" : ""}
                                        />
                                    </Tabs>
                                </div>
                                <div className="text-sm font-bold text-center items-center">
                                    {maestraData && (
                                        <span className="ml-2">
                                            {generateCopiableName(maestraData.rsk_ref, maestraData.embarcador, maestraData.nave, maestraData.voyage_number, maestraData.bl_awb, maestraData.container, maestraData.puerto_origen, maestraData.puerto_destino, maestraData.reclamo_usd, maestraData.reclamo_euros, maestraData.quickview_claim_survey?.selectedcurrency)}
                                        </span>
                                    )}
                                    {maestraData && (
                                        <button
                                            onClick={() => {
                                                const textToCopy = generateCopiableName(maestraData.rsk_ref, maestraData.embarcador, maestraData.nave, maestraData.voyage_number, maestraData.bl_awb, maestraData.container, maestraData.puerto_origen, maestraData.puerto_destino, maestraData.reclamo_usd, maestraData.reclamo_euros, maestraData.quickview_claim_survey?.selectedcurrency);
                                                navigator.clipboard.writeText(textToCopy);
                                            }}
                                            className="ml-2 p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-200"
                                            title="Copy to clipboard"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <AnimatePresence>
                                {selectedTab === "maestra" && (
                                    <motion.div
                                        key="maestra"
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -30 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <DetalleMaestraEditar maestraData={maestraData} aCargoOptions={aCargoOptions} />
                                    </motion.div>
                                )}
                                {selectedTab === "judicial" && (
                                    <motion.div
                                        key="judicial"
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -30 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <Card><CardBody><DetalleJudicialEditar judicialData={maestraData} /></CardBody></Card>
                                    </motion.div>
                                )}
                                {selectedTab === "quickview" && (
                                    <motion.div
                                        key="quickview"
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -30 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <DetalleQuickview
                                            rskRef={maestraData?.rsk_ref}
                                            claimId={maestraData?.id}
                                            isBusy={false}
                                            setIsBusy={() => {}}
                                        />
                                    </motion.div>
                                )}
                                {selectedTab === "claimletter" && (
                                    <motion.div
                                        key="claimletter"
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -30 }}
                                        transition={{ duration: 0.25 }}
                                    >
                                        <ClaimLetterTab maestraData={maestraData} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="danger" variant="light" onPress={closeMaestraModal}>
                                Close
                            </Button>
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>

            {/* Copy Previous Projection Confirmation Modal */}
            <Modal
                isOpen={isCopyPreviousModalOpen}
                onClose={() => setIsCopyPreviousModalOpen(false)}
                size="md"
            >
                <ModalContent>
                    <>
                        <ModalHeader>
                            <div className="flex flex-col gap-1">
                                <h3>Copy Previous Projection</h3>
                                <p className="text-sm text-gray-500">
                                    Are you sure you want to copy the previous projection data for {selectedQuarterForProjection?.rsk_ref}?
                                </p>
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            {selectedQuarterForProjection && (() => {
                                const projectionId = parseInt(selectedProjection);
                                const previousProjection = findPreviousProjection(selectedQuarterForProjection, projectionId);

                                if (!previousProjection) return null;

                                const previousPercent = previousProjection.quickview_percent
                                    ? (parseFloat(previousProjection.quickview_percent) * 100).toFixed(2) + '%'
                                    : 'N/A';
                                const previousDate = previousProjection.fecha_estimada || 'N/A';

                                return (
                                    <div className="flex flex-col gap-2">
                                        <p className="text-sm text-gray-700">
                                            The following data will be copied:
                                        </p>
                                        <div className="p-3 bg-gray-50 rounded-lg">
                                            <div className="flex justify-between mb-2">
                                                <span className="text-sm font-medium text-gray-600">Recovery Percentage:</span>
                                                <span className="text-sm text-gray-800">{previousPercent}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm font-medium text-gray-600">Estimated Date:</span>
                                                <span className="text-sm text-gray-800">{previousDate}</span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2">
                                            You can modify these values after copying.
                                        </p>
                                    </div>
                                );
                            })()}
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="danger"
                                variant="light"
                                onPress={() => setIsCopyPreviousModalOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleConfirmCopyPrevious}
                            >
                                Copy and Continue
                            </Button>
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>

            {/* Add Projection Modal */}
            <Modal
                isOpen={isAddProjectionModalOpen}
                onClose={closeAddProjectionModal}
                size="md"
                isDismissable={!isSavingProjection}
                hideCloseButton={isSavingProjection}
            >
                <ModalContent>
                    <>
                        <ModalHeader>
                            <div className="flex flex-col gap-1">
                                <h3>Add Projection</h3>
                                <p className="text-sm text-gray-500">
                                    Adding {projectionsList.find(p => p.id === parseInt(selectedProjection))?.title || 'Unknown'} projection for {selectedQuarterForProjection?.rsk_ref}
                                </p>
                                {selectedQuarterForProjection?.claimamount && (
                                    <p className="text-sm font-semibold text-blue-600">
                                        Claim Amount: ${new Intl.NumberFormat("es-CL", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(selectedQuarterForProjection.claimamount)}
                                    </p>
                                )}
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <div className="flex flex-col gap-4">
                                <Input
                                    label="Recovery Percentage"
                                    placeholder="e.g., 75"
                                    value={projectionForm.quickview_percent}
                                    onChange={(e) => setProjectionForm(prev => ({ ...prev, quickview_percent: e.target.value }))}
                                    type="number"
                                    min="0"
                                    max="100"
                                    endContent="%"
                                    description="Enter percentage as a number (0-100)"
                                    isDisabled={isSavingProjection}
                                />
                                {projectionForm.quickview_percent && selectedQuarterForProjection?.claimamount && (
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                        <p className="text-sm text-blue-700">
                                            <strong>Calculated Amount:</strong> ${new Intl.NumberFormat("es-CL", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            }).format((selectedQuarterForProjection.claimamount * parseFloat(projectionForm.quickview_percent)) / 100)}
                                        </p>
                                    </div>
                                )}
                                <Select
                                    label="Estimated Quarter"
                                    placeholder="Select quarter"
                                    selectedKeys={projectionForm.quarter ? [projectionForm.quarter] : []}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0];
                                        setProjectionForm(prev => ({ ...prev, quarter: selected || '' }));
                                    }}
                                    isDisabled={isSavingProjection}
                                >
                                    {quarterOptions.map((quarter) => (
                                        <SelectItem key={quarter.key} value={quarter.key}>
                                            {quarter.label}
                                        </SelectItem>
                                    ))}
                                </Select>
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="danger"
                                variant="light"
                                onPress={closeAddProjectionModal}
                                isDisabled={isSavingProjection}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleSaveProjection}
                                isLoading={isSavingProjection}
                                isDisabled={isSavingProjection}
                            >
                                {isSavingProjection ? 'Saving...' : 'Save Projection'}
                            </Button>
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>

            {/* Projections Modal (solo para filtro "All") */}
            <Modal
                isOpen={isProjectionsModalOpen}
                onClose={closeProjectionsModal}
                size="3xl"
                scrollBehavior="inside"
            >
                <ModalContent>
                    <>
                        <ModalHeader>
                            <div className="flex flex-col gap-1">
                                <h3>Projections for {selectedQuarterForProjections?.rsk_ref}</h3>
                                {selectedQuarterForProjections?.claimamount && (
                                    <p className="text-sm font-semibold text-blue-600">
                                        Claim Amount: ${new Intl.NumberFormat("es-CL", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(selectedQuarterForProjections.claimamount)}
                                    </p>
                                )}
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <div className="flex flex-col gap-4">
                                {/* QuickView Projection */}
                                {selectedQuarterForProjections?.quickview_projection && (
                                    <Card className="border-2 border-green-500">
                                        <CardBody>
                                            <div className="flex flex-col gap-2">
                                                <h4 className="text-lg font-semibold text-green-600">QuickView Projection</h4>
                                                <div className="grid grid-cols-2 gap-2 text-sm">
                                                    <div>
                                                        <span className="text-gray-600 text-green-700 font-medium">Recovery %:</span>{' '}
                                                        <span className="text-green-700 font-semibold">
                                                            {selectedQuarterForProjections.quickview_projection.quickview_percent
                                                                ? (parseFloat(selectedQuarterForProjections.quickview_projection.quickview_percent) * 100).toFixed(2) + '%'
                                                                : 'N/A'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-600 text-green-700 font-medium">Estimated Date:</span>{' '}
                                                        <span className="text-green-700 font-semibold">
                                                            {selectedQuarterForProjections.quickview_projection.fecha_estimada || 'N/A'}
                                                        </span>
                                                    </div>
                                                    {selectedQuarterForProjections.quickview_projection.proyeccion_usd && (
                                                        <div>
                                                            <span className="text-gray-600 text-green-700 font-medium">Projection USD:</span>{' '}
                                                            <span className="text-green-700 font-semibold">
                                                                ${new Intl.NumberFormat("es-CL", {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2,
                                                                }).format(selectedQuarterForProjections.quickview_projection.proyeccion_usd)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <Button
                                                    size="sm"
                                                    color="primary"
                                                    variant="flat"
                                                    onClick={() => handleEditQuickviewProjection(selectedQuarterForProjections)}
                                                    className="mt-2"
                                                >
                                                    Edit
                                                </Button>
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}

                                {/* Custom Projections */}
                                {selectedQuarterForProjections?.custom_projections && selectedQuarterForProjections.custom_projections.length > 0 && (
                                    <div className="flex flex-col gap-3">
                                        <h4 className="text-lg font-semibold">Custom Projections</h4>
                                        {[...selectedQuarterForProjections.custom_projections]
                                            .sort((a, b) => (b.projection_id || 0) - (a.projection_id || 0)) // Ordenar por projection_id DESC
                                            .map((proj, index) => (
                                                <Card key={proj.id || index} className="border">
                                                    <CardBody>
                                                        <div className="flex flex-col gap-2">
                                                            <h5 className="font-semibold">{proj.title}</h5>
                                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                                <div>
                                                                    <span className="text-gray-600">Recovery %:</span>{' '}
                                                                    <span className="font-semibold">
                                                                        {proj.quickview_percent
                                                                            ? (parseFloat(proj.quickview_percent) * 100).toFixed(2) + '%'
                                                                            : 'N/A'}
                                                                    </span>
                                                                </div>
                                                                <div>
                                                                    <span className="text-gray-600">Estimated Date:</span>{' '}
                                                                    <span className="font-semibold">{proj.fecha_estimada || 'N/A'}</span>
                                                                </div>
                                                                {proj.proyeccion_usd && (
                                                                    <div>
                                                                        <span className="text-gray-600">Projection USD:</span>{' '}
                                                                        <span className="font-semibold">
                                                                            ${new Intl.NumberFormat("es-CL", {
                                                                                minimumFractionDigits: 2,
                                                                                maximumFractionDigits: 2,
                                                                            }).format(proj.proyeccion_usd)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <span className="text-gray-600">Author:</span>{' '}
                                                                    <span className="font-semibold">{proj.author || 'N/A'}</span>
                                                                </div>
                                                            </div>
                                                            <Button
                                                                size="sm"
                                                                color="primary"
                                                                variant="flat"
                                                                onClick={() => handleEditCustomProjection(selectedQuarterForProjections, proj)}
                                                                className="mt-2"
                                                            >
                                                                Edit
                                                            </Button>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                    </div>
                                )}

                                {!selectedQuarterForProjections?.quickview_projection &&
                                 (!selectedQuarterForProjections?.custom_projections || selectedQuarterForProjections.custom_projections.length === 0) && (
                                    <p className="text-gray-500 text-center py-4">No projections available for this case.</p>
                                )}
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <Button color="danger" variant="light" onPress={closeProjectionsModal}>
                                Close
                            </Button>
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>

            {/* Complete Projection Modal */}
            <Modal
                isOpen={isCompleteProjectionModalOpen}
                onClose={closeCompleteProjectionModal}
                size="md"
                isDismissable={!isSavingProjection}
                hideCloseButton={isSavingProjection}
            >
                <ModalContent>
                    <>
                        <ModalHeader>
                            <div className="flex flex-col gap-1">
                                <h3>Complete Projection</h3>
                                <p className="text-sm text-gray-500">
                                    {projectionType === 'quickview_projection'
                                        ? 'Complete QuickView projection for'
                                        : `Complete ${projectionsList.find(p => p.id === parseInt(selectedProjection))?.title || 'projection'} for`}{' '}
                                    {selectedQuarterForProjection?.rsk_ref}
                                </p>
                                {selectedQuarterForProjection?.claimamount && (
                                    <p className="text-sm font-semibold text-blue-600">
                                        Claim Amount: ${new Intl.NumberFormat("es-CL", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(selectedQuarterForProjection.claimamount)}
                                    </p>
                                )}
                            </div>
                        </ModalHeader>
                        <ModalBody>
                            <div className="flex flex-col gap-4">
                                {projectionType === 'custom_projection' && (
                                    <Input
                                        label="Recovery Percentage"
                                        placeholder="e.g., 75"
                                        value={projectionForm.quickview_percent}
                                        onChange={(e) => setProjectionForm(prev => ({ ...prev, quickview_percent: e.target.value }))}
                                        type="number"
                                        min="0"
                                        max="100"
                                        endContent="%"
                                        description="Enter percentage as a number (0-100)"
                                        isDisabled={isSavingProjection}
                                    />
                                )}
                                {projectionType === 'quickview_projection' && (
                                    <div className="p-3 bg-green-50 rounded-lg">
                                        <p className="text-sm text-green-700">
                                            <strong>Note:</strong> Recovery percentage is automatically calculated for QuickView projection.
                                            You can only edit the estimated date.
                                        </p>
                                    </div>
                                )}
                                {projectionForm.quickview_percent && selectedQuarterForProjection?.claimamount && projectionType === 'custom_projection' && (
                                    <div className="p-3 bg-blue-50 rounded-lg">
                                        <p className="text-sm text-blue-700">
                                            <strong>Calculated Amount:</strong> ${new Intl.NumberFormat("es-CL", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            }).format((selectedQuarterForProjection.claimamount * parseFloat(projectionForm.quickview_percent)) / 100)}
                                        </p>
                                    </div>
                                )}
                                <Select
                                    label="Estimated Quarter"
                                    placeholder="Select quarter"
                                    selectedKeys={projectionForm.quarter ? [projectionForm.quarter] : []}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0];
                                        setProjectionForm(prev => ({ ...prev, quarter: selected || '' }));
                                    }}
                                    isDisabled={isSavingProjection}
                                >
                                    {quarterOptions.map((quarter) => (
                                        <SelectItem key={quarter.key} value={quarter.key}>
                                            {quarter.label}
                                        </SelectItem>
                                    ))}
                                </Select>
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="danger"
                                variant="light"
                                onPress={closeCompleteProjectionModal}
                                isDisabled={isSavingProjection}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                onPress={handleSaveCompleteProjection}
                                isLoading={isSavingProjection}
                                isDisabled={isSavingProjection}
                            >
                                {isSavingProjection ? 'Saving...' : 'Save'}
                            </Button>
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>
        </>
    );
}

export default BudgetTable;
